import {
  MaskedDateInput,
  MaskedDateInputProps,
  MaskedDateInputRef,
} from "./internal/MaskedDateInput";
import { DateMode } from "./types";
import { useFocusWithin } from "../../hooks/useFocusWithin";
import { useDateFieldSingleConversion } from "./internal/useDateFieldSingleConversion";
import { Popover } from "../Popover";
import { DateFieldSingleCalendar } from "./internal/DateFieldSingleCalendar";
import { useDateFieldOrchestration } from "./internal/useDateFieldOrchestration";
import { usePopoverSupport } from "../../internal/hooks/usePopoverSupport";
import { RefObject, useMemo, useRef, useState } from "react";
import { validateDate } from "./internal/utils";
import {
  useDateFieldSingleState,
  DateFieldSingleStateChangeHandler,
} from "./internal/useDateFieldSingleState";
import { useMergeRefs } from "../../hooks";
import { usePopoverCloseDelayWorkaround } from "../../internal";

export type DateFieldSingleChange = {
  /**
   * The date in ISO 8601 format.
   * @example "2025-07-02"
   */
  date: string | null;
  /**
   * Whether the input is valid. That is, it matches the format and is not empty.
   * This does not mean that the date is valid according to the constraints.
   */
  isInputValid: boolean;
  /**
   * Whether the input is empty.
   */
  isInputEmpty: boolean;
  /**
   * Whether the date is valid according to the constraints.
   * Constraints include:
   * - Required
   * - Unavailable dates
   * - Unavailable days of the week
   * - Min date
   * - Max date
   */
  isDateValid: boolean;
};

export type DateFieldSingleChangeHandler = (
  change: DateFieldSingleChange,
) => void;

export type DateFieldSingleProps = Omit<
  MaskedDateInputProps,
  | "onChange"
  | "value"
  | "autoComplete"
  | "onClick"
  | "onKeyDown"
  | "ref"
  | "lastValidDate"
  | "setLastValidDate"
  | "required"
  | "onFocus"
  | "onBlur"
> & {
  value?: string | null;
  defaultValue?: string | null;
  onChange?: DateFieldSingleChangeHandler;
  onFocus?: (event: FocusEvent) => void;
  onBlur?: (event: FocusEvent) => void;
  mode?: Extract<DateMode, "mm/dd/yyyy" | "dd/mm/yyyy">;
  disableHint?: boolean;
  disableCalendar?: boolean;
  unavailable?: {
    /**
     * The specific dates that are unavailable. ISO 8601 format.
     * @example ["2025-07-02"]
     */
    dates?: string[];
    /**
     * The days of the week that are unavailable.
     * (1-7, where 1 is Monday and 7 is Sunday)
     */
    daysOfWeek?: (1 | 2 | 3 | 4 | 5 | 6 | 7)[];
  };
  minDate?: string;
  maxDate?: string;
  required?: boolean;
};

export const DateFieldSingle = ({
  onFocus,
  onBlur,
  disableCalendar,
  required,
  mode,
  value: valueProp,
  defaultValue: defaultValueProp,
  minDate: minDateProp,
  maxDate: maxDateProp,
  unavailable: unavailableProp,
  onChange: onChangeProp,
  ...rest
}: DateFieldSingleProps) => {
  const inputRef = useRef<MaskedDateInputRef>(null);
  // We convert any props that deal in ISO 8601 strings to Luxon DateTime objects for easier internal use.
  const {
    value: normalizedValue,
    defaultValue: normalizedDefaultValue,
    minDate,
    maxDate,
    unavailable,
    onChange,
  } = useDateFieldSingleConversion({
    value: valueProp,
    defaultValue: defaultValueProp,
    onChange: onChangeProp,
    minDate: minDateProp,
    maxDate: maxDateProp,
    unavailable: unavailableProp,
  });

  const handleChange: DateFieldSingleStateChangeHandler = (change) => {
    onChange?.({
      ...change,
      isDateValid: validateDate({
        date: change.date,
        constraints: {
          required,
          unavailable,
          minDate: minDate ?? undefined,
          maxDate: maxDate ?? undefined,
        },
      }),
    });
  };

  // We use a hook to keep the value of input and the calendar in sync.
  const { value, handleInputChange, handleCalendarSelection } =
    useDateFieldSingleState({
      valueProp: normalizedValue,
      defaultValueProp: normalizedDefaultValue,
      inputRef,
      onChange: handleChange,
    });

  const { ref: wrapperRef } = useFocusWithin({
    onBlur,
    onFocus,
  });

  const [popoverTriggerRef, setPopoverTriggerRef] =
    useState<RefObject<HTMLInputElement>>();
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const combinedRef = useMergeRefs([popoverTriggerRef, inputRef]);
  const {
    calendarOpen,
    setCalendarOpen,
    handleCalendarKeyDown,
    handleInputKeyDown,
  } = useDateFieldOrchestration({
    inputRef,
    calendarDefaultOpen: false,
    popoverContentRef,
    disableCalendar,
  });

  const popoverSupported = usePopoverSupport();

  const shouldShowCalendar = usePopoverCloseDelayWorkaround(calendarOpen);

  const currentValidity = useMemo(
    () =>
      validateDate({
        date: value,
        constraints: {
          required,
          unavailable,
          minDate: minDate ?? undefined,
          maxDate: maxDate ?? undefined,
        },
      }),
    [value, required, unavailable, minDate, maxDate],
  );

  const justTheField = (
    <MaskedDateInput
      {...rest}
      mode={mode}
      ref={combinedRef}
      onChange={handleInputChange}
      onKeyDown={handleInputKeyDown}
      onClick={() => setCalendarOpen(true)}
      lastValidDate={value}
      required={required}
      autoComplete="off"
      data-date-valid={currentValidity}
    />
  );

  if (disableCalendar) {
    return justTheField;
  }

  if (!popoverSupported) {
    return justTheField;
  }

  return (
    <div ref={wrapperRef}>
      <Popover
        open={calendarOpen}
        modal
        placement="bottom-start"
        disableFlipFallback
        disableTriggerFocus
        onClose={() => setCalendarOpen(false)}
        disableAutoUpdate
        onClickOutside={() => setCalendarOpen(false)}
      >
        <Popover.Trigger>
          {({ ref: iRef }) => {
            setPopoverTriggerRef(iRef as RefObject<HTMLInputElement>);
            return justTheField;
          }}
        </Popover.Trigger>
        <Popover.Content ref={popoverContentRef} data-testid="calendar-popover">
          {shouldShowCalendar && (
            <DateFieldSingleCalendar
              onKeyDown={handleCalendarKeyDown}
              value={value}
              onSelection={handleCalendarSelection}
              minDate={minDate}
              maxDate={maxDate}
              unavailable={unavailable}
            />
          )}
        </Popover.Content>
      </Popover>
    </div>
  );
};

DateFieldSingle.displayName = "DateFieldSingle";
